import {
  Button,
  TableRow,
  MenuItem,
  TableCell,
  IconButton,
  Typography,
  ListItemText,
} from '@mui/material';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import { useBoolean } from 'src/hooks/use-boolean';
import { InviteCodeType, InviteCodeStatus } from 'src/type/invite-code';

import { fDate, fTime } from 'src/utils/format-time';

type Props = {
  row: InviteCodeType;
  onRejectCode: () => void;
};

export default function InviteCodeTableRow({ row, onRejectCode }: Props) {
  const popover = usePopover();
  const confirm = useBoolean();

  const { refCode, status, createdAt, refUsedAt, refToUser, refByUser } = row;

  const refToUserName = refToUser ? `${refToUser?.firstName} ${refToUser?.lastName}` : '-';

  return (
    <>
      <TableRow>
        <TableCell>
          <ListItemText
            primary={refCode}
            secondary={`ใช้โดย ${refToUserName}`}
            primaryTypographyProps={{ typography: 'body2', noWrap: true }}
            secondaryTypographyProps={{
              mt: 0.5,
              component: 'span',
              typography: 'caption',
            }}
          />
        </TableCell>

        <TableCell>
          <ListItemText
            primary={fDate(createdAt, 'DD MMMM BBBB')}
            secondary={fTime(createdAt)}
            primaryTypographyProps={{ typography: 'body2', noWrap: true }}
            secondaryTypographyProps={{
              mt: 0.5,
              component: 'span',
              typography: 'caption',
            }}
          />
        </TableCell>

        <TableCell>
          {refUsedAt ? (
            <ListItemText
              primary={fDate(refUsedAt)}
              secondary={fTime(refUsedAt)}
              primaryTypographyProps={{ typography: 'body2', noWrap: true }}
              secondaryTypographyProps={{
                mt: 0.5,
                component: 'span',
                typography: 'caption',
              }}
            />
          ) : (
            '-'
          )}
        </TableCell>

        <TableCell>
          <Typography variant="body2" noWrap>
            {refByUser?.firstName} {refByUser?.lastName}
          </Typography>
        </TableCell>

        <TableCell>
          <Label
            variant="soft"
            color={
              (status === InviteCodeStatus.ACCEPTED && 'success') ||
              (status === InviteCodeStatus.PENDING && 'warning') ||
              (status === InviteCodeStatus.REJECTED && 'error') ||
              'default'
            }
          >
            {status === InviteCodeStatus.ACCEPTED && 'ใช้งานแล้ว'}
            {status === InviteCodeStatus.PENDING && 'ยังไม่ใช้งาน'}
            {status === InviteCodeStatus.REJECTED && 'ยกเลิก'}
          </Label>
        </TableCell>

        <TableCell align="right" sx={{ px: 2 }}>
          <IconButton
            color={popover.open ? 'inherit' : 'default'}
            onClick={popover.onOpen}
            disabled={status !== InviteCodeStatus.PENDING}
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>
      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="top-right"
        sx={{ width: 160 }}
      >
        <MenuItem
          onClick={() => {
            popover.onClose();
            confirm.onTrue();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          ยกเลิก
        </MenuItem>
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="ยกเลิกรหัสเชิญ"
        content={`คุณต้องการยกเลิกรหัสเชิญ ${refCode} ใช่หรือไม่?`}
        action={
          <Button
            variant="contained"
            color="inherit"
            onClick={() => {
              onRejectCode();
              confirm.onFalse();
            }}
          >
            ดำเนินการต่อ
          </Button>
        }
      />
    </>
  );
}
